/**
 * Perform mandatory adjustments to the markdown content.
 * This function is invoked always (unlike `correctMarkdown`).
 */
export function preprocessMarkdown(inputMarkdown: string): string {
    const preprocessors = [
        processFixBrokenLinks,
        processLinks,
        processNonHttpLinks,
        processLineBreaks,
        processCodeInListItems,
    ];

    return preprocessors.reduce(
        (output, preprocess) => preprocess(output),
        inputMarkdown
    );
}

function processFixBrokenLinks(inputMarkdown: string): string {
    const fixers = [
        fixBrokenLinksMultipleLevels,
        fixBrokenLinksMalformed1,
        fixBrokenLinksMalformed2,
    ];
    return fixers.reduce((output, fixer) => fixer(output), inputMarkdown);
}

/**
 * Example input:
 *     Begin [Impero Link]([https://impero.com/foo?bar=12]\(https://impero.com/foo?bar=12\) "Impero Link") end
 *            ^-group1--^   ^-------group2--------------^                                ^^anchor        ^anchor
 *  Output:
 *      Begin [Impero Link](https://impero.com/foo?bar=12) end
 *
 *  Another example input (level 4):
 *     [Impero Link]([https://impero.com/foo?bar=12]\([https://impero.com/foo?bar=12]\([https://impero.com/foo?bar=12]\([https://impero.com/foo?bar=12]\(https://impero.com/foo?bar=12\)\)\)\) "Impero Link")
 *      ^-group1--^   ^-------group2--------------^                                                                                                                                   ^^^^^^^^anchor        ^anchor
 */
function fixBrokenLinksMultipleLevels(inputMarkdown: string): string {
    return inputMarkdown.replace(
        /\[([^\]]*)\]\(\[(https?:\/\/[^\]]*)\][^\)]*(?:\\\))+[^\)]*\)/gm,
        "[$1]($2)"
    );
}

/**
 * Example input:
 *     Begin [xy xy - 4-xy-xy]([https://xy/xy/15/xy/xy.xy?xy:xy=/xy%20xy/4_5_2xy4xy.xy) end
 *            ^-group1------^  ^-------group2----------------------------------------^^
 *                                                                                    \-anchor
 * Output:
 *    Begin [xy xy - 4-xy-xy](https://xy/xy/15/xy/xy.xy?xy:xy=/xy%20xy/4_5_2xy4xy.xy) end
 */
function fixBrokenLinksMalformed1(inputMarkdown: string): string {
    return inputMarkdown.replace(
        /\[([^\]]*)\]\(\[(https?:\/\/[^\]]*[^\)]+)\)/gm,
        "[$1]($2)"
    );
}

/** Example input
 *     Begin [**xy xy xy xy xy xy xy**](http://xy.xy.xy/xy.xy?xy=304148\&xy=xy]\([http://xy.xy.xy/xy.xy?xy=304148\&xy=xy]\(http://xy.xy.xy/xy.xy?xy=304148\&xy=xy "xy xy xy xy xy xy xy") end
 * Output:
 *     Begin [**xy xy xy xy xy xy xy**](http://xy.xy.xy/xy.xy?xy=304148\&xy=xy) end
 */
function fixBrokenLinksMalformed2(inputMarkdown: string): string {
    return inputMarkdown.replace(
        /\[([^\]]*)\]\((https?:\/\/[^\]]+)\]\\\([^\)]*\)/gm,
        "[$1]($2)"
    );
}

/**
 * Convert "foo\nbar" => "too  \nbar"
 *
 * NOTE: The old markdown render would render `<br>` for every single `\n`.
 * For the common markdown a single `\n` means a space, while `  \n` (two spaces and a newline) means a line break.
 * So in order to keep the same behavior for the markdown which already exists, we need to convert single `\n` to `  \n`.
 */
function processLineBreaks(inputMarkdown: string): string {
    return inputMarkdown.replace(
        /(\S)(?: )*(?:\n|\r|\r\n)(.)/g,
        (_match, startChar, endChar) => {
            return `${startChar}  \n${endChar}`;
        }
    );
}

/**
 * Convert raw links to markdown links.
 * Example:
 *    `https://example.com` => `[https://example.com](https://example.com)`
 *
 * Its purpose is to replace the Linkify plugin in the old Remarkable renderer.
 * The regex in function is designed to be idempotent.
 */
function processLinks(inputMarkdown: string): string {
    // Match an optional prefix (up to 3 characters of [ ] ( or \) followed by
    // either http://, https://, or www.
    const urlRegex = /([\[\]\(\\]{0,3})((?:https?:\/\/|www\.)[^\s\]<]+)/gm;
    let output = "";
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = urlRegex.exec(inputMarkdown)) !== null) {
        const [matchedText, prefix, url] = match;
        const matchIndex = match.index;
        // Append text between the previous match and this match.
        output += inputMarkdown.slice(lastIndex, matchIndex);

        // If the optional prefix indicates that the URL is already part of a markdown link,
        // do not wrap it.
        if (prefix === "[" || prefix === "](" || prefix === "]\\(") {
            output += matchedText;
            lastIndex = matchIndex + matchedText.length;
            // eslint-disable-next-line no-continue
            continue;
        }

        // --- Determine extra characters that should be considered part of the URL ---
        // We want to dynamically extend the matching URL in cases where there are trailing
        // characters (like extra parens) that the regex did not capture.
        const startIndex = matchIndex + prefix.length + url.length;
        let extraLength = 0;
        // Calculate current parenthesis balance.
        let parenBalance =
            (url.match(/\(/g) ?? []).length - (url.match(/\)/g) ?? []).length;

        while (startIndex + extraLength < inputMarkdown.length) {
            const char = inputMarkdown[startIndex + extraLength];
            // Stop if a whitespace or a markdown control character is encountered.
            if (/[\s\]<]/.test(char)) {
                break;
            }
            if (char === "(") {
                parenBalance++;
            } else if (char === ")") {
                parenBalance--;
            }
            extraLength++;
            // If we have reached a balanced state, check the next character.
            if (parenBalance === 0) {
                const nextChar = inputMarkdown[startIndex + extraLength];
                if (!nextChar || /[\s\]<\)]/.test(nextChar)) {
                    break;
                }
            }
        }

        // The full URL is the one captured by the regex plus any extra characters scanned.
        const fullUrl =
            url + inputMarkdown.slice(startIndex, startIndex + extraLength);

        // Escape parentheses in the link destination so the markdown parser
        // doesn't misinterpret them as link syntax.
        const escapedUrl = escapeParentheses(fullUrl);

        // Generate the markdown link.
        output += `${prefix}[${fullUrl}](${escapedUrl})`;
        // Update lastIndex so that the extra characters are "consumed" by our replacement.
        lastIndex = startIndex + extraLength;
    }
    // Append any remaining text.
    output += inputMarkdown.slice(lastIndex);
    return output;
}

/** Removes code blocks within list items.
 * Example input (at least 4 spaces before `Foo`):
 *     -     Foo
 * Output:
 *    - Foo
 */
function processCodeInListItems(input: string): string {
    return input.replace(/^(\s*)(-|\*|\+|\d+\.|\d+\))\s+/gm, "$1$2 ");
}

/** Replaces Markdown links that use non-http(s) protocols with plain text.
 *
 * This is a security-focused sanitizer that removes potentially dangerous or unsupported
 * protocols such as `javascript:`, `mailto:`, `ftp:`, or relative paths.
 *
 * For example:
 *   - `[Click here](javascript:alert\('XSS'\))` ->`Click here`
 *   - `[Email](mailto:someone@example.com)` ->`Email`
 *   - `[Docs](/internal/path)` -> unchanged (relative paths are not modified)
 *   - `[Google](https://google.com)` -> unchanged (https is OK)
 *
 * This function is idempotent and safe to run multiple times.
 */
export function processNonHttpLinks(input: string): string {
    let output = "";
    let i = 0;

    while (i < input.length) {
        // If `[` is not found or is found at the end of the input,
        // then append the rest of the input and break.
        const openBracket = input.indexOf("[", i);
        if (openBracket === -1 || openBracket + 1 >= input.length) {
            output += input.slice(i);
            break;
        }

        const closeBracket = input.indexOf("]", openBracket);
        if (
            closeBracket === -1 ||
            closeBracket + 1 >= input.length ||
            input[closeBracket + 1] !== "("
        ) {
            output += input.slice(i, closeBracket + 1 || input.length);
            i = closeBracket === -1 ? input.length : closeBracket + 1;
            // Skip to next loop iteration if this is not a valid markdown link.
            // eslint-disable-next-line no-continue
            continue;
        }

        // Write everything before the [text](url) segment
        output += input.slice(i, openBracket);

        const text = input.slice(openBracket + 1, closeBracket);
        const urlStart = closeBracket + 2;
        let urlEnd = urlStart;
        let parenDepth = 1;
        let inEscape = false;

        while (urlEnd < input.length) {
            const char = input[urlEnd];

            if (inEscape) {
                inEscape = false;
            } else if (char === "\\") {
                inEscape = true;
            } else if (char === "(") {
                parenDepth++;
            } else if (char === ")") {
                parenDepth--;
                if (parenDepth === 0) {
                    break;
                }
            }
            urlEnd += 1;
        }

        if (parenDepth !== 0) {
            // Unbalanced
            output += input.slice(openBracket, urlEnd + 1);
            i = urlEnd + 1;
            // Skip malformed link and continue parsing.
            // eslint-disable-next-line no-continue
            continue;
        }

        const fullUrlPart = input.slice(urlStart, urlEnd);
        const [rawUrl] = fullUrlPart.split(/\s+/); // Strip optional "title"
        const cleanUrl = rawUrl
            .replace(/\\\)/g, ")")
            .replace(/\\\(/g, "(")
            .trim();
        const protocol = getProtocol(cleanUrl);

        if (protocol && !["http", "https"].includes(protocol)) {
            // If protocol is preset but it's not http or https than convert the link
            // to plain text.
            output += text;
        } else {
            output += `[${text}](${fullUrlPart})`;
        }

        // Advance past the full match
        i = urlEnd + 1;
    }

    return output;
}

/**
 * Escape all parentheses in a URL so the markdown parser doesn't
 * misinterpret them as link syntax.
 */
function escapeParentheses(url: string): string {
    let result = "";
    for (const char of url) {
        if (char === "(" || char === ")") {
            result += "\\";
            result += char;
        } else {
            result += char;
        }
    }
    return result;
}

function getProtocol(url: string): string | null {
    if (url.includes(":")) {
        return url.split(":")[0].toLowerCase();
    } else {
        return null;
    }
}
